import { useState, useEffect, useMemo, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Switch } from '@/components/ui/switch'
import { Users, Plus, Edit, Trash2, AlertCircle, Save, X, ChevronDown, Search } from 'lucide-react'
import { ImageUpload } from './image-upload'

interface Resident {
  id: string
  name: string
  role: string
  email: string | null
  photo: string | null
  category: string | null
  is_active: boolean | null
  created_at: string
  updated_at: string
}

export function ResidentsManager() {
  const [residents, setResidents] = useState<Resident[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [togglingAll, setTogglingAll] = useState(false)
  const [error, setError] = useState('')
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [editingResident, setEditingResident] = useState<Resident | null>(null)
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    email: '',
    photo: '',
    category: '',
    is_active: true
  })
  const [existingCategories, setExistingCategories] = useState<string[]>([])
  const [showCustomCategory, setShowCustomCategory] = useState(false)
  const [categorySearch, setCategorySearch] = useState('')
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false)
  const categoryDropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    fetchResidents()
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(event.target as Node)
      ) {
        setIsCategoryDropdownOpen(false)
        setCategorySearch('')
      }
    }

    if (isCategoryDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isCategoryDropdownOpen])

  useEffect(() => {
    // Extract unique categories from residents
    const categories = Array.from(
      new Set(
        residents
          .map((r) => r.category)
          .filter((cat): cat is string => cat !== null && cat.trim() !== '')
      )
    ).sort()
    setExistingCategories(categories)
  }, [residents])

  const fetchResidents = async () => {
    try {
      const { data, error } = await supabase
        .from('residents')
        .select('*')
        .order('created_at', { ascending: false })

      if (error) throw error
      setResidents(data || [])
    } catch (err) {
      setError('Failed to fetch residents')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    setSaving(true)
    setError('')

    try {
      // Validate required fields
      if (!formData.name.trim() || !formData.role.trim()) {
        setError('Name and Role are required fields')
        setSaving(false)
        return
      }

      if (editingResident) {
        // Update existing
        const { error } = await supabase
          .from('residents')
          .update({
            name: formData.name.trim(),
            role: formData.role.trim(),
            email: formData.email.trim() || null,
            photo: formData.photo || null,
            category: formData.category.trim() || null,
            is_active: formData.is_active,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingResident.id)

        if (error) throw error
      } else {
        // Create new
        const { error } = await supabase
          .from('residents')
          .insert({
            name: formData.name.trim(),
            role: formData.role.trim(),
            email: formData.email.trim() || null,
            photo: formData.photo || null,
            category: formData.category.trim() || null,
            is_active: formData.is_active,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })

        if (error) throw error
      }

      await fetchResidents()
      handleCloseDialog()
    } catch (err) {
      setError('Failed to save resident data')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('residents')
        .delete()
        .eq('id', id)

      if (error) throw error
      await fetchResidents()
    } catch (err) {
      setError('Failed to delete resident')
    }
  }

  const handleEdit = (resident: Resident) => {
    setEditingResident(resident)
    const category = resident.category || ''
    setFormData({
      name: resident.name,
      role: resident.role,
      email: resident.email || '',
      photo: resident.photo || '',
      category: category,
      is_active: resident.is_active ?? true
    })
    // Show custom input if category doesn't exist in the list
    setShowCustomCategory(category !== '' && !existingCategories.includes(category))
    setIsDialogOpen(true)
  }

  const handleAddNew = () => {
    setEditingResident(null)
    setShowCustomCategory(false)
    setCategorySearch('')
    setIsCategoryDropdownOpen(false)
    setFormData({
      name: '',
      role: '',
      email: '',
      photo: '',
      category: '',
      is_active: true
    })
    setIsDialogOpen(true)
  }

  const handleCloseDialog = () => {
    setIsDialogOpen(false)
    setEditingResident(null)
    setShowCustomCategory(false)
    setCategorySearch('')
    setIsCategoryDropdownOpen(false)
    setFormData({
      name: '',
      role: '',
      email: '',
      photo: '',
      category: '',
      is_active: true
    })
  }

  // Check if all residents are active
  const allActive = useMemo(() => {
    if (residents.length === 0) return false
    return residents.every(resident => resident.is_active === true || resident.is_active === null)
  }, [residents])

  // Toggle all residents at once
  const handleToggleAll = async () => {
    if (residents.length === 0) return

    setTogglingAll(true)
    setError('')

    try {
      const newStatus = !allActive

      // Get all resident IDs
      const residentIds = residents.map(r => r.id)

      // Update all residents in batches if needed
      if (residentIds.length > 0) {
        const { error } = await supabase
          .from('residents')
          .update({ 
            is_active: newStatus,
            updated_at: new Date().toISOString()
          })
          .in('id', residentIds)

        if (error) throw error
      }

      await fetchResidents()
    } catch (err) {
      setError('Failed to toggle all residents')
    } finally {
      setTogglingAll(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Loading residents...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="space-y-2">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
            Community Management
          </h2>
          <p className="text-slate-600 text-lg">
            Manage village community members and their roles
          </p>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 w-full md:w-auto">
          <div className="flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 shadow-sm hover:shadow-md transition-shadow">
            <Switch
              checked={allActive}
              onCheckedChange={handleToggleAll}
              disabled={togglingAll || residents.length === 0}
              className="data-[state=checked]:bg-green-600"
            />
            <div className="flex flex-col min-w-[120px]">
              <span className={`text-sm font-semibold ${allActive ? 'text-green-600' : 'text-slate-500'}`}>
                {allActive ? 'All Active' : 'All Inactive'}
              </span>
              <span className="text-xs text-muted-foreground">
                {togglingAll ? 'Updating...' : 'Toggle all cards'}
              </span>
            </div>
          </div>
          <Button
            onClick={handleAddNew}
            className="w-full sm:w-auto bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Member
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5" />
            Community Members ({residents.length})
          </CardTitle>
          <CardDescription>
            Community members and their roles in the village
          </CardDescription>
        </CardHeader>
        <CardContent>
          {residents.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No community members found</p>
              <Button onClick={handleAddNew} className="mt-4">
                <Plus className="w-4 h-4 mr-2" />
                Add First Member
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                  <TableRow>
                    <TableHead>Member</TableHead>
                    <TableHead className="w-28">Status</TableHead>
                    <TableHead className="w-28 text-right">Actions</TableHead>
                  </TableRow>
              </TableHeader>
              <TableBody>
                {residents.map((resident) => (
                  <TableRow key={resident.id}>
                    {/* Name, role and email in one cell — three separate columns
                        made the table wide without adding information. */}
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="w-9 h-9">
                          <AvatarImage src={resident.photo || ''} />
                          <AvatarFallback>
                            {resident.name.split(' ').map(n => n[0]).join('')}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0">
                          <div className="font-medium truncate">{resident.name}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            {[resident.role, resident.email].filter(Boolean).join(' · ')}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={resident.is_active ? "default" : "secondary"}>
                        {resident.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(resident)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="outline" size="sm">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                            <AlertDialogTitle>Delete community member?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This action cannot be undone. The selected community member record will be removed.
                            </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                                onClick={() => handleDelete(resident.id)}
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingResident ? 'Edit Community Member' : 'Add New Community Member'}
            </DialogTitle>
            <DialogDescription>
              {editingResident ? 'Update community member information' : 'Add a new community member'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Enter full name"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Role/Position *</Label>
                <Input
                  id="role"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  placeholder="e.g., Deputy Sarpanch"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="resident@village.gov.in"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <div className="relative">
                {!showCustomCategory ? (
                  <div className="relative" ref={categoryDropdownRef}>
                    <div
                      className="flex h-9 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background cursor-pointer hover:bg-muted/50"
                      onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                    >
                      <span className={formData.category ? 'text-foreground' : 'text-muted-foreground'}>
                        {formData.category || 'Select or type a category (optional)'}
                      </span>
                      <ChevronDown className={`h-4 w-4 opacity-50 transition-transform ${isCategoryDropdownOpen ? 'rotate-180' : ''}`} />
                    </div>
                    {isCategoryDropdownOpen && (
                      <div className="absolute z-50 w-full mt-1 rounded-md border bg-popover shadow-md">
                        <div className="p-2 border-b">
                          <div className="relative">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                              placeholder="Search categories..."
                              value={categorySearch}
                              onChange={(e) => setCategorySearch(e.target.value)}
                              className="pl-8 h-8"
                              autoFocus
                            />
                          </div>
                        </div>
                        <div className="max-h-[200px] overflow-y-auto">
                          {existingCategories
                            .filter((cat) =>
                              cat.toLowerCase().includes(categorySearch.toLowerCase())
                            )
                            .map((cat) => (
                              <div
                                key={cat}
                                className="px-3 py-2 text-sm cursor-pointer hover:bg-muted/50"
                                onClick={() => {
                                  setFormData({ ...formData, category: cat })
                                  setIsCategoryDropdownOpen(false)
                                  setCategorySearch('')
                                }}
                              >
                                {cat}
                              </div>
                            ))}
                          {existingCategories.filter((cat) =>
                            cat.toLowerCase().includes(categorySearch.toLowerCase())
                          ).length === 0 && (
                            <div className="px-3 py-2 text-sm text-muted-foreground">
                              No categories found
                            </div>
                          )}
                          <div
                            className="px-3 py-2 text-sm cursor-pointer hover:bg-muted/50 border-t"
                            onClick={() => {
                              setShowCustomCategory(true)
                              setIsCategoryDropdownOpen(false)
                              setCategorySearch('')
                            }}
                          >
                            + Add New Category
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      id="category"
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      placeholder="Type new category name"
                      list="category-suggestions"
                    />
                    <datalist id="category-suggestions">
                      {existingCategories.map((cat) => (
                        <option key={cat} value={cat} />
                      ))}
                    </datalist>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowCustomCategory(false)
                        setFormData({ ...formData, category: '' })
                      }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="is_active">Active Status</Label>
              <div className="flex items-center gap-2 rounded-md border border-input px-3 py-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <span className="text-sm text-muted-foreground">
                  {formData.is_active ? 'Card will be visible on the site' : 'Card will be hidden'}
                </span>
              </div>
            </div>

            <ImageUpload
              currentImage={formData.photo || ''}
              onImageChange={(imageUrl) => setFormData({ ...formData, photo: imageUrl })}
              folder="residents"
              label="Member Photo"
              description="Upload a photo of the community member"
              circular={true}
            />

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={handleCloseDialog}>
                <X className="w-4 h-4 mr-2" />
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
